import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { SHORTLIST_CARD } from '../../lib/card-fields.ts';
import { api, type PipelineView, type ShortlistMemberView } from '../../lib/client.ts';
import { useListView } from '../../lib/list-view.ts';
import { stageRefOf, useMembers } from '../../lib/record-data.ts';
import { CardFieldList, CardFieldsMenu } from '../boards/card-fields.tsx';
import { KanbanBoard, type BoardSource } from '../boards/kanban-board.tsx';
import { ViewsMenu } from '../record-table/views-menu.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';

type Stage = PipelineView['stages'][number];
type Openings = { title: string; openings: number; filled: number };

/**
 * A shortlist's candidates as a board by Shortlist Flow stage (RELAY-1038). Columns page from
 * `/shortlists/{id}/candidates:search` and a drag goes through the same PATCH as the shortlist
 * table, so it writes the candidate's stage history, audit and activity like any other move.
 * Card fields are a saved view of their own (`shortlist_candidates`); the view state lives in
 * the component, since this board sits inside a project page rather than having a URL.
 *
 * A drop into a won stage (Hired) first asks what kind of hire it is (TS-925): a new hire fills
 * one of the job's openings, and is not offered when they are all filled (the API refuses it
 * too); a replacement refills a position someone already filled and takes none.
 */
export function ShortlistBoard({
  shortlistId,
  jobId,
  job,
  pipeline,
  canMove,
  onMoved,
}: {
  shortlistId: string;
  jobId: string;
  /** The job's openings, for the hire question. */
  job: Openings;
  pipeline: PipelineView;
  canMove: boolean;
  /** After a move: the job's `filled` (and its bundle's slots) may have changed. */
  onMoved?: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [hiring, setHiring] = useState<{ name: string; stage: Stage } | null>(null);
  const answer = useRef<((a: { extra?: unknown } | null) => void) | null>(null);
  const settle = (a: { extra?: unknown } | null) => {
    answer.current?.(a);
    answer.current = null;
    setHiring(null);
  };
  const { nameOf } = useMembers();
  const [encoded, setEncoded] = useState<string | undefined>(undefined);
  const { state, setState, activeViewId } = useListView({
    entity: 'shortlist_candidates',
    encoded,
    write: setEncoded,
  });
  const chosen = state.card?.fields ?? SHORTLIST_CARD.defaults;
  const source = useMemo<BoardSource<ShortlistMemberView>>(
    () => ({
      scope: [shortlistId],
      counts: () => api.jobs.shortlistCounts(shortlistId, {}),
      page: (stageId, cursor, limit) =>
        api.jobs.shortlistMembers(shortlistId, {
          filter: { field: 'stageId', op: 'eq', value: stageId },
          limit,
          ...(cursor ? { cursor } : {}),
        }),
      move: (m, stageId, extra) =>
        api.jobs.moveMember(
          shortlistId,
          m.candidateId,
          stageId,
          (extra as { replacement?: boolean } | undefined)?.replacement,
        ),
      confirm: (m, stage) =>
        stage.terminalKind === 'won' && m.stage.terminalKind !== 'won'
          ? new Promise((resolve) => {
              answer.current = resolve;
              setHiring({ name: m.candidate.name, stage });
            })
          : Promise.resolve({}),
      idOf: (m) => m.candidateId,
      stageOf: (m) => m.stageId,
      titleOf: (m) => m.candidate.name,
      moved: (m, stage) => ({
        ...m,
        stageId: stage.id,
        stage: stageRefOf(stage),
        stageEnteredAt: new Date().toISOString(),
      }),
    }),
    [shortlistId],
  );
  return (
    <div className="flex flex-col gap-2" data-testid="shortlist-board">
      <div className="flex justify-end gap-2">
        <CardFieldsMenu
          fields={SHORTLIST_CARD.fields}
          chosen={chosen}
          onChange={(fields) => setState({ ...state, card: { fields } })}
        />
        <ViewsMenu
          entity="shortlist_candidates"
          state={state}
          activeId={activeViewId}
          onApply={(next, id) => setState(next, id)}
        />
      </div>
      <KanbanBoard
        name={`shortlist-${shortlistId}`}
        pipeline={pipeline}
        source={source}
        canMove={canMove}
        onMoved={async () => {
          await qc.invalidateQueries({ queryKey: ['job-shortlists', jobId] });
          await onMoved?.();
        }}
        renderCard={(m) => (
          <CardFieldList item={m} set={SHORTLIST_CARD} chosen={chosen} ctx={{ nameOf }} />
        )}
      />
      <HireDialog hiring={hiring} job={job} onAnswer={settle} />
    </div>
  );
}

/** "New hire or replacement?" for a drop into a won stage; closing it cancels the move. */
function HireDialog({
  hiring,
  job,
  onAnswer,
}: {
  hiring: { name: string; stage: Stage } | null;
  job: Openings;
  onAnswer: (a: { extra?: unknown } | null) => void;
}) {
  const full = job.filled >= job.openings;
  return (
    <Dialog open={hiring !== null} onOpenChange={(open) => !open && onAnswer(null)}>
      <DialogContent data-testid="hire-dialog">
        <DialogHeader>
          <DialogTitle>
            Move {hiring?.name} to {hiring?.stage.name}
          </DialogTitle>
          <DialogDescription>
            {job.filled} of {job.openings} opening{job.openings === 1 ? '' : 's'} of {job.title}{' '}
            filled.{' '}
            {full
              ? 'Every opening is filled: this can only be a replacement, or add an opening first.'
              : 'A new hire fills one; a replacement refills a position someone already filled.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onAnswer(null)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => onAnswer({ extra: { replacement: true } })}>
            Replacement
          </Button>
          <Button disabled={full} onClick={() => onAnswer({ extra: { replacement: false } })}>
            New hire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
